import { get } from 'lodash';
import { Fragment, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useCurrentUserContext } from '../../../../hooks/useCurrentUserContext/useCurrentUserContext';
import { useModal } from '../../../../hooks/useModal/useModal';
import { useWorkflowPathname } from '../../../../hooks/useWorkflowPathname/useWorkflowPathname';
import { ApplicationDataInterface } from '../../../../pages/Applications/applicationData.interface';
import useAnalyticStore from '../../../../store/analyticStore/useAnalyticStore';
import { defaultModalButtons, Modal } from '../../../shared/Modal/Modal';
import { ContactUs } from '../../ContactUs/ContactUs';
import { QueuedSubmissionActionCard } from '../QueuedSubmissionActionCard/QueuedSubmissionActionCard';
import { QueuedModalTypeEnum } from './enums/QueuedModalTypeEnum';

export const QueuedSubmissionTaskCard = ({
  state,
}: {
  state: ApplicationDataInterface;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const workflowName = useWorkflowPathname();
  const [currentUser] = useCurrentUserContext();
  const { isShowing, open } = useModal();

  const [queuedModalType, setQueuedModalType] =
    useState<QueuedModalTypeEnum | null>(null);

  const learnModalTitle = 'Working on your application';
  const learnModalSubtitle = 'Learn more';
  const learnModalContent =
    "In some cases, finalizing an application may take a little extra time (usually just a few minutes). Once it's complete, we'll clearly signal the next steps. If it seems like the process is taking longer than expected, feel free to reach out to our team using the “Contact Us” button on the previous screen. We're here to help!";

  const { sendApplicationTaskButtonClickedAnalytics } = useAnalyticStore();

  const applicationStatus = get(
    state,
    'currentVehicle.checklightSession.status',
  );

  const primaryButton = {
    name: 'contactUs',
    children: 'Contact us',
    onClick: () => {
      sendApplicationTaskButtonClickedAnalytics(
        applicationStatus,
        primaryButton.children,
      );

      setQueuedModalType(QueuedModalTypeEnum.CONTACT);
      open();
    },
    order: 1,
  };

  const secondaryButton = {
    name: 'learnMore',
    children: 'Learn More',
    onClick: () => {
      sendApplicationTaskButtonClickedAnalytics(
        applicationStatus,
        secondaryButton.children,
      );

      setQueuedModalType(QueuedModalTypeEnum.LEARN);
      open();
    },
    order: 2,
  };

  const taskButtons = useMemo(() => {
    return {
      secondaryButton,
      primaryButton,
    };
  }, [
    currentUser,
    location.pathname,
    navigate,
    open,
    primaryButton,
    secondaryButton.children,
    secondaryButton.name,
    state,
    workflowName,
  ]);

  return (
    <Fragment>
      <QueuedSubmissionActionCard buttonGroup={taskButtons} />

      {isShowing && queuedModalType === QueuedModalTypeEnum.CONTACT && (
        <ContactUs />
      )}

      {isShowing && queuedModalType === QueuedModalTypeEnum.LEARN && (
        <Modal
          buttonGroupPresModel={defaultModalButtons()}
          subtitle={learnModalSubtitle}
          title={learnModalTitle}
        >
          {learnModalContent}
        </Modal>
      )}
    </Fragment>
  );
};
